<template>
  <v-app-bar
    id="documentToolbar"
    class="border-s-0 border-e-0 border-t-0"
    elevation="2"
    extension-height="40"
    height="64"
    theme="dark"
  >
    <template #prepend>
      <BackToDeepboxButton
        v-if="
          isAuthenticated &&
          !isDocumentAuthAuthenticated &&
          !isDocumentPublicRoute
        "
      />
    </template>
    <template #title>
      <div class="d-flex align-center text-title-small">
        <v-divider
          v-if="
            isAuthenticated &&
            !isDocumentAuthAuthenticated &&
            !isDocumentPublicRoute &&
            !isBatchRoute
          "
          class="mr-4 title-divider"
          color="white"
          length="18"
          opacity="25"
          thickness="2"
          vertical
        />
        <div v-else class="pa-2"></div>
        <div
          v-if="!isEditing"
          class="document-title-wrapper d-flex align-center"
          @mouseenter="canEditDocumentName && (isHovered = true)"
          @mouseleave="isHovered = false"
        >
          <span class="text-truncate cursor-default">{{
            truncate(displayTitle, 80, '')
          }}</span>
          <v-btn
            v-if="canEditDocumentName"
            v-show="isHovered"
            class="ml-1"
            data-test-id="edit-document-name"
            density="compact"
            icon
            size="small"
            variant="text"
            @click.stop="startEditing"
          >
            <v-icon size="16">fal fa-pen</v-icon>
          </v-btn>
        </div>
        <v-text-field
          v-else
          ref="titleInputRef"
          v-model="editTitle"
          class="document-title-input"
          density="compact"
          hide-details
          single-line
          variant="outlined"
          @blur="saveEditing"
          @keydown.enter.prevent="saveEditing"
          @keydown.esc.prevent="cancelEditing"
          @keydown.tab.prevent="saveEditing"
        />
      </div>
    </template>
    <template #append>
      <div class="document-toolbar-append deep-caption text-right">
        <DocumentFileSwitcher :document="document" />
      </div>
    </template>
    <!-- Batch (and other callers) can still inject a status strip under the
         app bar. Document signed/withdrawn/rejected lives in PdfPreview so
         it tracks the PDF column as PdfSidebar opens/closes. -->
    <template v-if="$slots['status-toolbar']" #extension>
      <slot name="status-toolbar" />
    </template>
  </v-app-bar>
</template>

<script setup lang="ts">
import BackToDeepboxButton from '@/components/buttons/BackToDeepboxButton.vue';
import AuthService from '@/services/authService.ts';
import { computed, nextTick, type PropType, ref } from 'vue';
import { useRoute } from 'vue-router';
import { truncate } from '@/composables/useUtils';
import { useDocumentAuth } from '@/composables/useDocumentAuth';
import { useSignStore } from '@/stores/sign.ts';
import { useCreateStore } from '@/stores/create.ts';
import type { Document, DocumentRequest } from '@/types/Document';
import DocumentFileSwitcher from '@/components/documents/DocumentFileSwitcher.vue';
import { useApi } from '@/composables/useApi';

const props = defineProps({
  document: {
    type: Object as PropType<Document>,
    default: undefined
  }
});
const route = useRoute();
const documentAuth = useDocumentAuth();
const isDocumentAuthAuthenticated = computed(() => {
  return documentAuth.isAuthenticated.value;
});

const isAuthenticated = computed(() => AuthService.isAuthenticated);
const signStore = useSignStore();

const createStore = useCreateStore();
const { deepSignAPI } = useApi();

const isHovered = ref(false);
const isEditing = ref(false);
const editTitle = ref('');
const titleInputRef = ref<InstanceType<
  typeof import('vuetify/components').VTextField
> | null>(null);

const hasMultipleFiles = computed(() => {
  return (props.document?.files?.length ?? 0) > 1;
});

const displayTitle = computed(() => {
  const name = props.document?.documentName || '';

  // Envelope names are user-defined and shown verbatim — a ".pdf" suffix stays
  // because the user chose it. Single-file documents aren't renameable —
  // always show the .pdf extension.
  if (!hasMultipleFiles.value && name && !name.endsWith('.pdf')) {
    return `${name}.pdf`;
  }

  return name;
});

function startEditing() {
  if (!canEditDocumentName.value) return;
  editTitle.value = displayTitle.value;
  isEditing.value = true;
  isHovered.value = false;
  nextTick(() => {
    titleInputRef.value?.focus();
  });
}

async function saveEditing() {
  if (!isEditing.value) return;
  isEditing.value = false;

  const trimmedTitle = editTitle.value.trim();
  if (!trimmedTitle || trimmedTitle === displayTitle.value) return;

  const documentId = props.document?.documentId;
  if (!documentId) return;

  try {
    await deepSignAPI.value.documents.updateById(documentId, {
      documentName: trimmedTitle
    } as DocumentRequest);

    // Keep local store state in sync
    if (createStore.document?.documentId === documentId) {
      createStore.document.documentName = trimmedTitle;
    }
    if (signStore.document?.documentId === documentId) {
      signStore.document.documentName = trimmedTitle;
    }
  } catch (error) {
    console.error('Failed to update document name:', error);
  }
}

function cancelEditing() {
  isEditing.value = false;
  editTitle.value = displayTitle.value;
}

const isDraftMode = computed(
  () => route.name === 'document-create' || route.name === 'p-document-create'
);

// Editing the document name is only allowed in draft mode, and only when the
// document bundles more than one file — a single-file document's name isn't
// meaningful to rename independently of its one file.
const canEditDocumentName = computed(
  () => isDraftMode.value && (props.document?.files?.length ?? 0) > 1
);

const isDocumentPublicRoute = computed(
  () => route.name === 'p-document-create' || route.name === 'p-document-view'
);

const isBatchRoute = computed(
  () => route.name === 'document-batch-sign' || route.name === 'document-batch'
);
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/styles/settings.scss' as *;

:deep(.v-toolbar__extension) {
  padding: 0;
}

@media #{map.get($display-breakpoints, 'xl-and-up')} {
  #documentToolbar {
    width: calc(100% - 484px) !important;
  }
}

.document-toolbar-append {
  display: flex;
  align-items: center;
  gap: 12px;
}

.document-title-wrapper {
  min-width: 0;
  max-width: 100%;
}

// Vuetify's vertical divider defaults to align-self: stretch, which anchors
// it to the top of the flex row instead of centering. That's invisible while
// the row is short (plain title text), but once the row grows taller (the
// outlined edit input), the divider stays pinned at the top instead of
// staying centered against the row.
.title-divider {
  align-self: center;
}

.document-title-input {
  min-width: 200px;
  max-width: 400px;

  :deep(.v-field__input) {
    color: white;
    font-size: inherit;
  }

  :deep(.v-field__outline) {
    --v-field-border-opacity: 0.5;
  }
}
</style>
